import React from 'react';

/**
 * OperisLoader — Unified clock-themed loading spinner for the entire app.
 *
 * Props:
 *  - size     : 'xs' | 'sm' | 'md' | 'lg' | 'xl'  (default: 'md')
 *  - message  : string  (optional text below the spinner)
 *  - className: string  (extra wrapper classes)
 *  - fullPage : boolean (centres vertically within parent, default false)
 *  - color    : string  (accent CSS colour, default '#6366f1' — indigo)
 *  - light    : boolean (use lighter colours for dark background contexts)
 */

const sizeMap = {
  xs: { box: 20,  stroke: 1.6, handW: 1.4, dot: 1.6,  textCls: 'text-[10px]' },
  sm: { box: 32,  stroke: 2,   handW: 1.6, dot: 2,    textCls: 'text-xs'     },
  md: { box: 56,  stroke: 2.4, handW: 2,   dot: 3,    textCls: 'text-sm'     },
  lg: { box: 80,  stroke: 3,   handW: 2.4, dot: 4,    textCls: 'text-sm'     },
  xl: { box: 112, stroke: 3.5, handW: 3,   dot: 5,    textCls: 'text-base'   },
};

const OperisLoader = ({
  size = 'md',
  message,
  className = '',
  fullPage = false,
  color = '#6366f1',
  light = false,
}) => {
  const cfg  = sizeMap[size] || sizeMap.md;
  const vb   = 100; // SVG viewBox is always 0 0 100 100
  const cx   = 50;
  const cy   = 50;
  const r    = 42;  // clock face radius

  // 12 hour tick marks
  const ticks = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const isMajor = i % 3 === 0;
    const outerR = r - 1;
    const innerR = r - (isMajor ? 10 : 6);
    return {
      x1: cx + outerR * Math.cos(angle),
      y1: cy + outerR * Math.sin(angle),
      x2: cx + innerR * Math.cos(angle),
      y2: cy + innerR * Math.sin(angle),
      isMajor,
    };
  });

  const faceColor   = light ? 'rgba(255,255,255,0.12)' : 'rgba(99,102,241,0.06)';
  const rimColor    = light ? 'rgba(255,255,255,0.3)' : 'rgba(99,102,241,0.25)';
  const tickColor   = light ? 'rgba(255,255,255,0.5)' : 'rgba(99,102,241,0.35)';
  const handColor   = color;
  const secondColor = light ? '#f472b6' : '#ec4899';

  return (
    <div className={`inline-flex flex-col items-center justify-center gap-2 ${fullPage ? 'py-20 w-full' : ''} ${className}`}>
      <svg
        width={cfg.box}
        height={cfg.box}
        viewBox={`0 0 ${vb} ${vb}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Loading"
        role="status"
      >
        {/* Glow filter */}
        <defs>
          <filter id="operis-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Clock face */}
        <circle cx={cx} cy={cy} r={r} fill={faceColor} stroke={rimColor} strokeWidth={cfg.stroke} />

        {/* Tick marks */}
        {ticks.map((t, i) => (
          <line
            key={i}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={tickColor}
            strokeWidth={t.isMajor ? cfg.stroke * 0.8 : cfg.stroke * 0.4}
            strokeLinecap="round"
          />
        ))}

        {/* Minute hand — spins slowly (4s) */}
        <line
          x1={cx} y1={cy} x2={cx} y2={cy - r * 0.6}
          stroke={handColor}
          strokeWidth={cfg.handW}
          strokeLinecap="round"
          filter="url(#operis-glow)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`0 ${cx} ${cy}`}
            to={`360 ${cx} ${cy}`}
            dur="4s"
            repeatCount="indefinite"
          />
        </line>

        {/* Second hand — spins faster (1.5s) */}
        <line
          x1={cx} y1={cy + r * 0.12}
          x2={cx} y2={cy - r * 0.78}
          stroke={secondColor}
          strokeWidth={cfg.handW * 0.6}
          strokeLinecap="round"
          opacity="0.8"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`0 ${cx} ${cy}`}
            to={`360 ${cx} ${cy}`}
            dur="1.5s"
            repeatCount="indefinite"
          />
        </line>

        {/* Centre dot */}
        <circle cx={cx} cy={cy} r={cfg.dot} fill={handColor} />
        <circle cx={cx} cy={cy} r={cfg.dot * 0.45} fill={light ? '#1e1b4b' : '#ffffff'} />

        {/* Orbiting ring pulse (only for md+ sizes) */}
        {size !== 'xs' && size !== 'sm' && (
          <circle
            cx={cx} cy={cy} r={r + 3}
            fill="none"
            stroke={handColor}
            strokeWidth="0.8"
            opacity="0.3"
          >
            <animate attributeName="r"    values={`${r + 1};${r + 6};${r + 1}`} dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.08;0.3"               dur="2s" repeatCount="indefinite" />
          </circle>
        )}
      </svg>

      {/* Message */}
      {message && (
        <p className={`${cfg.textCls} font-medium text-gray-500 dark:text-gray-400 animate-pulse`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default OperisLoader;
